import React, { useState, useEffect, useMemo } from 'react';
import { Sparkles, X } from 'lucide-react';
import resonanceData from '../data/resonance.json';
import booksData from '../data/books.json';

const Resonance = () => {
  const [resonancePairs, setResonancePairs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [displayCount, setDisplayCount] = useState(9);
  const [activePair, setActivePair] = useState(null);

  useEffect(() => {
    setResonancePairs(resonanceData);
  }, []);

  // Build book lookup map
  const bookMap = useMemo(() => {
    const map = {};
    booksData.forEach((book) => {
      map[book.id] = book;
    });
    return map;
  }, []);

  // Extract unique categories for filter bar
  const categories = useMemo(() => {
    const categorySet = new Set(['All']);
    resonanceData.forEach((item) => {
      if (item.category) {
        categorySet.add(item.category);
      }
    });
    // 定义显示顺序
    const categoryOrder = [
      'All',
      'Inner Peace & Anxiety',
      'Power & Strategy',
      'Relationships & Connection',
      'Destiny & Change',
      'Growth & Mastery',
      'Human Nature',
      'Wisdom & Life'
    ];
    return Array.from(categorySet).sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, []);

  // Filter by category
  const filteredPairs = useMemo(() => {
    if (selectedCategory === 'All') {
      return resonancePairs;
    }
    return resonancePairs.filter((pair) => pair.category === selectedCategory);
  }, [resonancePairs, selectedCategory]);

  // Get displayed pairs
  const displayedPairs = useMemo(() => {
    return filteredPairs.slice(0, displayCount);
  }, [filteredPairs, displayCount]);

  const handleOpenModal = (pair) => {
    setActivePair(pair);
  };

  const handleCloseModal = () => {
    setActivePair(null);
  };

  const handleShowMore = () => {
    setDisplayCount(prev => prev + 9);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setDisplayCount(9); // Reset display count when changing category
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const extractUrlFromMarkdownLink = (markdownLink) => {
    if (!markdownLink) return null;
    const raw = String(markdownLink).trim();
    // Handles markdown style: [text](https://...)
    const match = raw.match(/\((https?:\/\/[^)]+)\)/);
    if (match) return match[1];
    // Also allow plain URLs
    if (/^https?:\/\//i.test(raw)) return raw;
    return null;
  };

  return (
    <div className="min-h-screen bg-dark-slate py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif text-porcelain-white mb-4">
            East Meets West
          </h1>
          <p className="text-lg text-porcelain-white/70 max-w-2xl mx-auto">
            Discover the profound connections between Eastern and Western wisdom
          </p>
        </div>

        {/* Sticky Category Filter Bar (Horizontal Scroll) */}
        <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 mb-8 md:mb-12 backdrop-blur-md bg-gray-950/80 border-b border-white/5">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 flex-nowrap">
            {categories.map((category) => {
              const isActive = selectedCategory === category;
              return (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-sm transition-colors ${
                    isActive
                      ? 'bg-white text-black font-medium'
                      : 'bg-transparent border border-white/20 text-gray-400 hover:text-white'
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>
        </div>

        {/* Masonry Grid Layout */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
          {displayedPairs.map((pair) => (
            <div key={pair.id} className="break-inside-avoid mb-6">
              <div
                className="rounded-2xl border border-white/10 bg-gray-950/40 p-5 shadow-xl cursor-pointer hover:border-white/20 hover:bg-gray-900/60 transition-colors"
                onClick={() => handleOpenModal(pair)}
              >
                {/* Question as Main Title + Tags */}
                <div className="mb-5">
                  {pair.question && (
                    <h3 className="text-gray-100 text-lg sm:text-xl font-serif mb-3 leading-snug">
                      {pair.question}
                    </h3>
                  )}
                  {pair.tags && pair.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {pair.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 rounded-full text-xs border border-white/10 text-gray-300 bg-white/5"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* East / West (Short Quotes) */}
                <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                  {/* East Card */}
                  <div className="rounded-xl p-4 border border-red-900/50 bg-gradient-to-br from-[#2c0b0e] to-[#1a0506]">
                    <div className="flex items-baseline justify-between gap-3 mb-3">
                      <h3 className="text-white text-xs font-semibold tracking-wide uppercase">East</h3>
                      <p className="text-gray-400 text-[11px] font-medium">{pair.east.author}</p>
                    </div>
                    <blockquote className="text-gray-100 font-serif text-sm leading-relaxed line-clamp-3">
                      {pair.east.text}
                    </blockquote>
                  </div>

                  {/* West Card */}
                  <div className="rounded-xl p-4 border border-slate-800 bg-gradient-to-br from-[#0f172a] to-[#020617]">
                    <div className="flex items-baseline justify-between gap-3 mb-3">
                      <h3 className="text-white text-xs font-semibold tracking-wide uppercase">West</h3>
                      <p className="text-gray-400 text-[11px] font-medium">{pair.west.author}</p>
                    </div>
                    <blockquote className="text-gray-100 font-serif text-sm leading-relaxed line-clamp-3">
                      {pair.west.text}
                    </blockquote>
                  </div>
                </div>

                {/* Key Difference Hook + Action Button */}
                <div className="mt-5 pt-4 border-t border-white/10 flex flex-col gap-3">
                  {pair.key_difference && (
                    <div>
                      <p className="text-xs font-semibold text-amber-300 tracking-wide uppercase mb-1">
                        Key Difference
                      </p>
                      <p className="text-sm text-gray-200 leading-relaxed">
                        {pair.key_difference}
                      </p>
                    </div>
                  )}
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(pair);
                      }}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-white/20 bg-white/5 text-white text-sm font-medium hover:bg-white/10 hover:shadow-[0_0_26px_rgba(248,113,113,0.35)] transition-all"
                    >
                      <span className="text-lg">✨</span>
                      <span>Explore the Fusion</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Show More Button */}
        {displayedPairs.length < filteredPairs.length && (
          <div className="text-center mt-12">
            <button
              onClick={handleShowMore}
              className="px-8 py-3 rounded-full border border-white/15 bg-white/5 text-white hover:bg-white/10 hover:shadow-[0_0_26px_rgba(255,255,255,0.12)] transition-all font-medium"
            >
              Show More ({filteredPairs.length - displayedPairs.length} remaining)
            </button>
          </div>
        )}

        {/* Deep Dive Modal */}
        {activePair && (
          <div className="fixed inset-0 z-40 flex items-center justify-center px-4 sm:px-6 lg:px-8">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={handleCloseModal}
            />
            <div className="relative z-50 max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 shadow-2xl">
              {/* Modal Header */}
              <div className="flex items-start justify-between gap-4 px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-white/10">
                <div>
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white border border-white/20 mb-3">
                    {activePair.theme}
                  </div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-serif text-white leading-snug">
                    {activePair.question}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="p-2 rounded-full bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Modal Body (Scrollable) */}
              <div className="px-6 sm:px-8 py-6 sm:py-8 space-y-8 overflow-y-auto max-h-[calc(90vh-4rem)] pb-24">
                {/* Section 1: Comparison */}
                <section>
                  <h3 className="text-sm font-semibold tracking-[0.16em] uppercase text-gray-400 mb-4">
                    The Comparison
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {/* East */}
                    <div className="rounded-2xl p-5 border border-red-900/60 bg-gradient-to-br from-[#2c0b0e] via-[#1a0506] to-black">
                      <div className="flex items-baseline justify-between gap-3 mb-3">
                        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-red-200/80">
                          East
                        </p>
                        <p className="text-xs text-red-100/80 font-medium text-right">
                          {activePair.east.author}
                        </p>
                      </div>
                      <blockquote className="text-gray-50 font-serif text-base leading-relaxed mb-3">
                        “{activePair.east.text}”
                      </blockquote>
                      {activePair.east.meaning && (
                        <p className="text-sm text-red-100/90 leading-relaxed">
                          {activePair.east.meaning}
                        </p>
                      )}
                    </div>

                    {/* West */}
                    <div className="rounded-2xl p-5 border border-slate-700 bg-gradient-to-br from-slate-900 via-slate-950 to-black">
                      <div className="flex items-baseline justify-between gap-3 mb-3">
                        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-sky-200/80">
                          West
                        </p>
                        <p className="text-xs text-sky-100/80 font-medium text-right">
                          {activePair.west.author}
                        </p>
                      </div>
                      <blockquote className="text-gray-50 font-serif text-base leading-relaxed mb-3">
                        “{activePair.west.text}”
                      </blockquote>
                      {activePair.west.meaning && (
                        <p className="text-sm text-sky-100/90 leading-relaxed">
                          {activePair.west.meaning}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Section 2: Fusion */}
                {activePair.fusion && (
                  <section>
                    <h3 className="text-sm font-semibold tracking-[0.16em] uppercase text-gray-400 mb-3 flex items-center gap-2">
                      <Sparkles size={16} className="text-amber-300" />
                      <span>The Fusion</span>
                    </h3>
                    <div className="rounded-2xl border border-amber-200/30 bg-gradient-to-r from-amber-500/10 via-pink-500/10 to-sky-500/10 shadow-[0_0_40px_rgba(251,191,36,0.18)]">
                      <div className="p-5 sm:p-6">
                        <p className="text-base sm:text-lg text-amber-50 leading-relaxed font-serif">
                          {activePair.fusion}
                        </p>
                      </div>
                    </div>
                  </section>
                )}

                {/* Section 3: Practical Application */}
                {(activePair.real_life || activePair.try_this) && (
                  <section>
                    <h3 className="text-sm font-semibold tracking-[0.16em] uppercase text-gray-400 mb-4">
                      Practical Application
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      {activePair.real_life && (
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">🏙️</span>
                            <p className="text-xs font-semibold tracking-[0.16em] uppercase text-gray-300">
                              Real-life Example
                            </p>
                          </div>
                          <p className="text-sm text-gray-100 leading-relaxed">
                            {activePair.real_life}
                          </p>
                        </div>
                      )}

                      {activePair.try_this && (
                        <div className="rounded-2xl border border-emerald-300/60 bg-gradient-to-br from-emerald-500/10 via-emerald-400/10 to-emerald-500/5 p-5 shadow-[0_0_32px_rgba(16,185,129,0.25)]">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">💡</span>
                            <p className="text-xs font-semibold tracking-[0.16em] uppercase text-emerald-100">
                              Try This
                            </p>
                          </div>
                          <p className="text-sm sm:text-base text-emerald-50 leading-relaxed font-medium">
                            {activePair.try_this}
                          </p>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Footer: Further Reading */}
                {activePair.books && activePair.books.length > 0 && (
                  <section className="mt-8">
                    <h3 className="text-xs font-mono text-gray-500 tracking-widest uppercase mb-3">
                      Further Reading
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {activePair.books.map((bookId) => {
                        // Per-item lookup from books.json (fallback to map)
                        const bookDetails = booksData.find((b) => b.id === bookId) || bookMap[bookId];
                        if (!bookDetails) return null;
                        const url = extractUrlFromMarkdownLink(bookDetails.amazon_link);

                        return (
                          <a
                            key={bookId}
                            href={url || '#'}
                            target={url ? "_blank" : undefined}
                            rel={url ? "noopener noreferrer" : undefined}
                            className="group flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-3 md:p-4 hover:bg-white/10 hover:border-white/20 transition-all cursor-pointer"
                          >
                            {bookDetails.cover_image ? (
                              <img
                                src={bookDetails.cover_image}
                                alt={bookDetails.title}
                                className="w-10 h-14 object-cover rounded border border-white/10 flex-shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-14 rounded bg-black/40 border border-white/10 overflow-hidden flex items-center justify-center text-[10px] text-gray-300 uppercase tracking-wide flex-shrink-0">
                                {bookDetails.title.split('(')[0].trim()}
                              </div>
                            )}
                            <div className="flex flex-col min-w-0 flex-1">
                              <p className="font-serif font-bold text-gray-200 group-hover:text-amber-200 line-clamp-1">
                                {bookDetails.title}
                              </p>
                              {bookDetails.one_liner && (
                                <p className="text-xs text-gray-500 line-clamp-1 mt-1">
                                  {bookDetails.one_liner}
                                </p>
                              )}
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Resonance;
